import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconVolume, IconVolumeOff } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import VideoDropZone from '../../components/video/VideoDropZone';
import useVideoPaste from '../../components/video/useVideoPaste';
import { VideoFileRow } from '../../components/video/VideoFileRow';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import {
    DEFAULT_VOLUME_PERCENT,
    MAX_VOLUME_PERCENT,
    MAX_VOLUME_VIDEO_BYTES,
    MIN_VOLUME_PERCENT,
    adjustVideoVolume,
    formatFileSize,
    isSupportedVideoFile,
    volumeVideoName,
} from './volumeVideoEngine';
import {
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';

export default function VolumeVideo() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [file, setFile] = useState(null);
    const [volumePercent, setVolumePercent] = useState(DEFAULT_VOLUME_PERCENT);
    const [audioMuted, setAudioMuted] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const videoPreviewUrl = useMemo(
        () => (file ? URL.createObjectURL(file) : ''),
        [file],
    );

    const resultPreviewUrl = useMemo(
        () => (result ? URL.createObjectURL(result.blob) : ''),
        [result],
    );

    useEffect(() => () => {
        if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    }, [videoPreviewUrl]);

    useEffect(() => () => {
        if (resultPreviewUrl) URL.revokeObjectURL(resultPreviewUrl);
    }, [resultPreviewUrl]);

    const addFile = useCallback((incoming) => {
        const list = Array.from(incoming || []);
        const videos = list.filter(isSupportedVideoFile);
        if (!videos.length) {
            const msg = t('volumeVideo.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        const next = videos[0];
        if (next.size > MAX_VOLUME_VIDEO_BYTES) {
            const msg = t('volumeVideo.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        setFile(next);
        setResult(null);
        setError(null);
        setProgress(null);
        setVolumePercent(DEFAULT_VOLUME_PERCENT);
        setAudioMuted(false);
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page, the same as the image and PDF
    // tools. Ignored while a run is in flight: the clip would be dropped
    // on the floor by the reset that follows it.
    useVideoPaste(addFile, { enabled: !processing });

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFile(e.dataTransfer.files);
    };

    const handleReset = () => {
        setFile(null);
        setResult(null);
        setError(null);
        setProgress(null);
        setVolumePercent(DEFAULT_VOLUME_PERCENT);
        setAudioMuted(false);
    };

    const invalidateResult = () => {
        setResult(null);
        setError(null);
        setProgress(null);
    };

    const resolveErrorMessage = (err) => {
        const code = err?.message;
        if (code === 'OUT_OF_MEMORY') return t('volumeVideo.errors.outOfMemory');
        if (code === 'ENCODE_FAILED' || code === 'EMPTY_OUTPUT' || code === 'VOLUME_FAILED') {
            return t('volumeVideo.errors.failed');
        }
        return t('volumeVideo.errors.failed');
    };

    const handleAdjustVolume = async () => {
        if (!file || processing) return;

        recordToolUsage('video/volume', { category: 'video' });

        setProcessing(true);
        setResult(null);
        setError(null);
        setProgress(0);

        try {
            const blob = await adjustVideoVolume(
                file,
                volumePercent,
                (value) => setProgress(value),
                { removeAudio: audioMuted },
            );

            const output = {
                name: volumeVideoName(file.name, { muted: audioMuted, volumePercent }),
                originalSize: file.size,
                outputSize: blob.size,
                muted: audioMuted,
                volumePercent,
                blob,
            };
            setResult(output);

            notifications.show({
                title: t('common.success'),
                message: audioMuted
                    ? t('volumeVideo.successMuted')
                    : t('volumeVideo.successVolume', { value: toBnDigits(volumePercent) }),
                color: 'green',
            });
        } catch (err) {
            const msg = resolveErrorMessage(err);
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
            setProgress(null);
        }
    };

    const status = deriveToolStatus({
        hasInput: !!file,
        canRun: !!file,
        processing,
        hasResult: !!result,
        hasError: !!error,
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`volumeVideo.howTo.step${i}`)),
        [t],
    );

    const volumeLabel = audioMuted
        ? t('volumeVideo.mutedBadge')
        : t('volumeVideo.volumeBadge', { value: toBnDigits(volumePercent) });

    const sizeNote = result ? (
        <>
            <span className="font-semibold text-[#3D3833] dark:text-zinc-200">
                {formatFileSize(result.originalSize)}
            </span>
            <span className="text-[#A8A099] dark:text-zinc-500">→</span>
            <span className="font-semibold text-[#1E9E5E]">
                {formatFileSize(result.outputSize)}
            </span>
            <span className="text-[#A8A099] dark:text-zinc-500">
                ({result.muted
                    ? t('volumeVideo.mutedBadge')
                    : t('volumeVideo.volumeBadge', { value: toBnDigits(result.volumePercent) })})
            </span>
        </>
    ) : null;

    const statusText = getToolStatusText(status, t, {
        ready: t('toolPage.statusText.ready'),
        success: t('volumeVideo.done'),
    });

    const resultDownloads = result ? [{
        id: result.name,
        name: result.name,
        originalSize: formatFileSize(result.originalSize),
        compressedSize: formatFileSize(result.outputSize),
        onDownload: () => downloadBlob(result.blob, result.name),
    }] : undefined;

    return (
        <ToolPageLayout
            slug="video/volume"
            subtitleKey="volumeVideo.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('toolPage.files', { count: toBnDigits(file ? 1 : 0) })}
            showAdd={false}
            howToSteps={howToSteps}
            onReset={file || result || error ? handleReset : undefined}
            primaryAction={{
                label: audioMuted ? t('volumeVideo.muteAction') : t('volumeVideo.apply'),
                onClick: handleAdjustVolume,
                disabled: !file || processing || !!result,
                loading: processing,
                icon: audioMuted
                    ? <IconVolumeOff size={18} stroke={1.9} />
                    : <IconVolume size={18} stroke={1.9} />,
            }}
            controls={(
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="video/*,.mp4,.mov,.avi,.mkv,.webm,.m4v"
                        className="hidden"
                        onChange={(e) => { addFile(e.target.files); e.target.value = ''; }}
                    />

                    {!file ? (
                        <VideoDropZone
                            title={t('volumeVideo.dropTitle')}
                            hint={t('volumeVideo.dropHint')}
                            isDragging={isDragging}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                        />
                    ) : (
                        <VideoFileRow
                            name={file.name}
                            meta={formatFileSize(file.size)}
                            onRemove={handleReset}
                            removeLabel={t('volumeVideo.removeFile')}
                        />
                    )}
                </>
            )}
            settings={file ? (
                <ToolCard title={t('volumeVideo.settingsTitle')}>
                    <div className="space-y-4">
                        <label className="flex items-start gap-3 rounded-xl border border-[#ECE8E4] bg-white px-3 py-3 dark:border-zinc-700 dark:bg-zinc-900">
                            <input
                                type="checkbox"
                                checked={audioMuted}
                                onChange={(e) => {
                                    setAudioMuted(e.target.checked);
                                    invalidateResult();
                                }}
                                disabled={processing}
                                className="mt-0.5 h-4 w-4 rounded border-[#ECE8E4] text-violet-600 focus:ring-violet-500 dark:border-zinc-700"
                            />
                            <span>
                                <span className="block text-[13px] font-semibold text-[#3D3833] dark:text-zinc-100">
                                    {t('volumeVideo.muteLabel')}
                                </span>
                                <span className="mt-0.5 block text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                    {t('volumeVideo.muteHint')}
                                </span>
                            </span>
                        </label>

                        <label htmlFor="volume-video-level" className="block space-y-1.5">
                            <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                {t('volumeVideo.levelLabel', { value: volumeLabel })}
                            </span>
                            <input
                                id="volume-video-level"
                                type="range"
                                min={MIN_VOLUME_PERCENT}
                                max={MAX_VOLUME_PERCENT}
                                step={5}
                                value={volumePercent}
                                onChange={(e) => {
                                    setVolumePercent(Number(e.target.value));
                                    invalidateResult();
                                }}
                                disabled={audioMuted || processing}
                                className="w-full accent-violet-500 disabled:opacity-40"
                            />
                            <div className="flex justify-between text-[10.5px] text-[#A8A099] dark:text-zinc-500">
                                <span>{toBnDigits(0)}%</span>
                                <span>{toBnDigits(50)}%</span>
                                <span>{toBnDigits(100)}%</span>
                                <span>{toBnDigits(200)}%</span>
                                <span>{toBnDigits(300)}%</span>
                            </div>
                            <span className="block text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                {t('volumeVideo.levelHint')}
                            </span>
                        </label>
                    </div>
                </ToolCard>
            ) : null}
            preview={(
                <ToolPreviewPanel
                    title={t('volumeVideo.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('volumeVideo.previewEmpty')}
                    idleHint={t('volumeVideo.previewEmptyHint')}
                    processingLabel={t('volumeVideo.processing')}
                    progress={progress ?? undefined}
                    error={error}
                    successLabel={t('volumeVideo.done')}
                    downloadLabel={t('volumeVideo.download')}
                    downloads={resultDownloads}
                    onAgain={result ? handleReset : undefined}
                    againLabel={t('volumeVideo.startOver')}
                    sizeNote={sizeNote}
                    readyHint={status === 'ready' ? (
                        <ToolReadyHint actionLabel={audioMuted ? t('volumeVideo.muteAction') : t('volumeVideo.apply')} />
                    ) : null}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showIdle={!file && !error}
                >
                    {file && !processing && !error ? (
                        <div className="flex h-full min-h-[280px] flex-col">
                            <div className="flex flex-1 items-center justify-center overflow-hidden rounded-xl border border-[#ECE8E4] bg-black dark:border-zinc-700">
                                <video
                                    src={result ? resultPreviewUrl : videoPreviewUrl}
                                    controls
                                    className="max-h-[360px] w-full max-w-full"
                                />
                            </div>
                            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                <span className="truncate font-medium text-[#3D3833] dark:text-zinc-300">
                                    {result ? result.name : file.name}
                                </span>
                                <span>
                                    {result
                                        ? formatFileSize(result.outputSize)
                                        : volumeLabel}
                                </span>
                            </div>
                        </div>
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
